// src/modules/requerimiento/views/RequerimientoMisPendientes.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  InputAdornment,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// Services
import { getReqSolicitudes } from "../services/reqSolicitudes";
import { getProfile } from "../services/auth";
// Hook para usuarios TRS
import { useUsuariosTrs } from "../hooks/useUsuariosTrs";

// Nuevo componente presentacional
import RequerimientoPendientesList from "../components/RequerimientoPendientesList";

export default function RequerimientoMisPendientes() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [page, setPage] = useState(1); // 1-based (MUI Pagination)
  // 9 = tres filas completas en la grilla de 3 columnas.
  const [perPage, setPerPage] = useState(9);

  // Lo que se escribe (texto) se manda al backend (query) 350 ms después de la última tecla.
  const [texto, setTexto] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (texto === query) return undefined;
    const t = setTimeout(() => setQuery(texto), 350);
    return () => clearTimeout(t);
  }, [texto, query]);

  const [tecnicosFilter, setTecnicosFilter] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Hook de usuarios TRS
  const { usuarios, loadingUsuarios } = useUsuariosTrs({});

  // Carga perfil para saber quién es el usuario actual
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const user = await getProfile();
        if (!mounted) return;
        setCurrentUser(user);
        setTecnicosFilter(user?.username ?? "");
      } catch (err) {
        console.error("Error obteniendo perfil:", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Cuando ya tenemos usuarios + currentUser, seteamos el usuario seleccionado por defecto
  useEffect(() => {
    if (!currentUser || !usuarios.length) return;
    const found = usuarios.find((u) => u.username === currentUser.username);
    setSelectedUser(found || null);
    setTecnicosFilter(found?.username || currentUser.username || "");
  }, [currentUser, usuarios]);

  // 🔁 Función de carga con paginación de servidor
  const fetchData = async ({
    pageArg = page,
    pageSizeArg = perPage,
    tecnicosArg = tecnicosFilter,
    queryArg = query,
  } = {}) => {
    setLoading(true);
    setErrorMsg("");

    try {
      const params = {
        page: pageArg, // backend espera 1-based
        pageSize: pageSizeArg,
        pendiente: "true", // mis pendientes => solo pendientes
      };

      if (tecnicosArg && tecnicosArg.trim()) {
        params.tecnicos = tecnicosArg.trim();
      }

      if (queryArg && queryArg.trim()) {
        params.buscar = queryArg.trim();
      }

      const { data } = await getReqSolicitudes(params);
      const rows = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];

      setItems(rows);
      setTotalItems(Number(data?.total ?? rows.length) || 0);
      setPage(pageArg);
      setPerPage(pageSizeArg);
    } catch (err) {
      console.error("Error cargando requerimientos:", err);
      setErrorMsg("No se pudieron cargar los requerimientos.");
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos cuando cambia el técnico
  useEffect(() => {
    if (!tecnicosFilter) return;
    // siempre volvemos a la página 1 al cambiar técnico
    fetchData({ pageArg: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tecnicosFilter]);

  // Cargar también cuando cambie el texto de búsqueda
  useEffect(() => {
    if (!tecnicosFilter) return;
    fetchData({ pageArg: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleOpen = (cod) => navigate(`/requerimiento/${cod}`);

  const pageCount = Math.max(1, Math.ceil(totalItems / perPage));

  return (
      <Box >
        {/* === FILA 2: FILTROS Y BÚSQUEDA === */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
          {/* Columna 1: Filtro de Técnico (Autocomplete) */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Autocomplete
              size="small"
              options={usuarios}
              loading={loadingUsuarios}
              value={selectedUser}
              onChange={(_, newValue) => {
                setSelectedUser(newValue);
                setTecnicosFilter(newValue?.username || "");
                setPage(1);
              }}
              getOptionLabel={(option) =>
                option?.nombre
                  ? `${option.nombre} (${option.username})`
                  : option?.username || ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtrar por Técnico"
                  placeholder="Buscar técnico..."
                />
              )}
              clearOnEscape
              fullWidth
            />
          </Grid>

          {/* Columna 2: Buscador General (manda 'buscar' al backend) */}
          <Grid size={{ xs: 12, sm: 6, md: 8 }}>
            <TextField
              size="small"
              placeholder="Buscar por cliente, descripción, código, observación, etc."
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        {/* === FIN FILTROS === */}

        {!loading && !errorMsg && totalItems > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {totalItems === 1 ? "1 requerimiento pendiente" : `${totalItems} requerimientos pendientes`}
            {selectedUser ? ` de ${selectedUser.nombre || selectedUser.username}` : ""}
          </Typography>
        )}

        {/* === LISTA + PAGINACIÓN (componente separado) === */}
        <RequerimientoPendientesList
          items={items}
          loading={loading}
          errorMsg={errorMsg}
          page={page}
          pageCount={pageCount}
          totalItems={totalItems}
          onPageChange={(newPage) => {
            fetchData({ pageArg: newPage });
          }}
          onOpen={handleOpen}
        />
      </Box>
  );
}
